import CustomLoadingButton from "@/components/Button/CustomLoadingButton";
import CardContainer from "@/components/Card/CardContainer";
import { useUserContext } from "@/context/UserContext";
import { isUserBillingAdmin } from "@/utils/user";
import { Box, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AccountPage, AccountQuotaData, AccountUserData } from ".";
import QuotaUsageCard from "./components/QuotaUsageCard";

export default function AccountDetail({
  userData,
  quotaData,
  setActivePage,
  isLoading,
}: AccountDetailProps) {
  const { user, doesPreviousUserExists } = useUserContext();
  const isBillingAdmin = isUserBillingAdmin(user, doesPreviousUserExists);
  const navigate = useNavigate();

  const handleManageBilling = () => {
    navigate("/dashboard/billing");
  };

  return (
    <Stack spacing={4}>
      {/* Header */}
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h3Semi">Account Information</Typography>
      </Box>

      {/* User Info Section */}
      <CardContainer sx={{ p: 3 }} shadow={false} secondaryColor>
        <Typography variant="body1Semi" sx={{ mb: 2, color: "text.secondary" }}>
          Profile Details
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography
                variant="captionReg"
                color="text.secondary"
                component="div"
              >
                Username
              </Typography>
              {isLoading ? (
                <Skeleton variant="text" width={150} sx={{ mt: 0.5 }} />
              ) : (
                <Typography variant="body1Reg" component="div" sx={{ mt: 0.5 }}>
                  {userData.username || "-"}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography
                variant="captionReg"
                color="text.secondary"
                component="div"
              >
                Company
              </Typography>
              {isLoading ? (
                <Skeleton variant="text" width={120} sx={{ mt: 0.5 }} />
              ) : (
                <Typography variant="body1Reg" component="div" sx={{ mt: 0.5 }}>
                  {userData.companyName || "-"}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box>
              <Typography
                variant="captionReg"
                color="text.secondary"
                component="div"
              >
                Email
              </Typography>
              {isLoading ? (
                <Skeleton variant="text" width={250} sx={{ mt: 0.5 }} />
              ) : (
                <Typography variant="body1Reg" component="div" sx={{ mt: 0.5 }}>
                  {userData.email || "-"}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContainer>

      {/* Campaign Impressions Usage Card */}
      {isLoading ? (
        <CardContainer sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Skeleton variant="text" width={180} height={24} />
            <Skeleton variant="circular" width={20} height={20} />
          </Box>
          <Skeleton variant="text" width={120} height={48} />
          <Skeleton
            variant="rounded"
            width="100%"
            height={10}
            sx={{ mt: 2, borderRadius: 5 }}
          />
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}
          >
            <Skeleton variant="text" width={100} />
            <Skeleton variant="text" width={60} />
          </Box>
        </CardContainer>
      ) : (
        quotaData.campaignImpressions && (
          <QuotaUsageCard
            title="Campaign Impressions"
            usage={quotaData.campaignImpressions.usage}
            total={quotaData.campaignImpressions.total}
            limit={quotaData.campaignImpressions.limit}
            rollover={quotaData.campaignImpressions.rollover}
            isUnlimited={quotaData.campaignImpressions.isUnlimited}
          />
        )
      )}

      {/* Other Limits Section - only show if at least one limit exists */}
      {isLoading ? (
        <CardContainer sx={{ p: 3 }} shadow={false} secondaryColor>
          <Skeleton variant="text" width={100} sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Skeleton variant="text" width={100} />
              <Skeleton variant="text" width={60} sx={{ mt: 0.5 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Skeleton variant="text" width={160} />
              <Skeleton variant="text" width={60} sx={{ mt: 0.5 }} />
            </Grid>
          </Grid>
        </CardContainer>
      ) : (
        (quotaData.advertisersLimit || quotaData.linkedinProfilesLimit) && (
          <CardContainer sx={{ p: 3 }} shadow={false} secondaryColor>
            <Typography
              variant="body1Semi"
              sx={{ mb: 2, color: "text.secondary" }}
            >
              Other Limits
            </Typography>
            <Grid container spacing={3}>
              {quotaData.advertisersLimit && (
                <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                  <Box sx={{ overflow: "hidden" }}>
                    <Typography
                      variant="captionReg"
                      color="text.secondary"
                      component="div"
                    >
                      Advertisers Limit
                    </Typography>
                    <Typography
                      variant="body1Reg"
                      component="div"
                      sx={{ mt: 0.5 }}
                    >
                      {quotaData.advertisersLimit}
                    </Typography>
                  </Box>
                </Grid>
              )}
              {quotaData.linkedinProfilesLimit && (
                <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                  <Box sx={{ overflow: "hidden" }}>
                    <Typography
                      variant="captionReg"
                      color="text.secondary"
                      component="div"
                    >
                      LinkedIn Profiles To Resolve
                    </Typography>
                    <Typography
                      variant="body1Reg"
                      component="div"
                      sx={{ mt: 0.5 }}
                    >
                      {quotaData.linkedinProfilesLimit}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContainer>
        )
      )}

      {/* Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        {isBillingAdmin && (
          <CustomLoadingButton
            title="Manage Billing"
            isLoading={false}
            onClick={handleManageBilling}
          />
        )}
        <CustomLoadingButton
          title="Change Password"
          isLoading={false}
          onClick={() => setActivePage("Form")}
        />
      </Box>
    </Stack>
  );
}

interface AccountDetailProps {
  userData: AccountUserData;
  quotaData: AccountQuotaData;
  setActivePage: React.Dispatch<React.SetStateAction<AccountPage>>;
  isLoading: boolean;
}
